import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { MoreHorizontal, Trash2, Eye, ArrowBigRightDash } from "lucide-react";
import { Button } from "@/components/ui/button";
import { AuditPlanListItem } from "@/lib/audit-plan";
import { useMutation, UseMutationResult, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { deleteAuditPlanById, deferAuditPlanById } from "@/lib/audit-plan";
import { Link } from "@tanstack/react-router";
import { cn } from "@/lib/utils";

const ICON_CLASS = "mr-2 h-4 w-4";
const BUTTON_BASE_CLASS = "flex h-full gap-2";

type Actions = "show_detail" | "delete" | "defer";
type NonEmptyArray<T> = [T, ...T[]];
export type OnActionCompletedFn = (action: Actions) => void;

type Props = {
  auditPlan: AuditPlanListItem;
  selectedActions: NonEmptyArray<Actions>;
  onActionCompleted: OnActionCompletedFn;
};

export const AuditPlanActions = ({
  auditPlan,
  selectedActions,
  onActionCompleted,
}: Props) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const deleteMutation = useMutation({
    mutationFn: (id: string) => {
      return deleteAuditPlanById(id);
    },
    onSuccess: () => {
      onActionCompleted("delete");
      toast({ title: "Success", description: "Audit plan deleted." });
      queryClient.invalidateQueries({ queryKey: ["auditPlans"] });
    },
  });

  const deferMutation = useMutation({
    mutationFn: (id: string) => {
      return deferAuditPlanById(id);
    },
    onSuccess: () => {
      toast({ title: "Success", description: "Audit plan has been deferred." });
      queryClient.invalidateQueries({ queryKey: ["auditPlans"] });
    },
  });

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon">
          <MoreHorizontal className="h-5 w-5" />
          <span className="sr-only">Actions</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {hasAction(selectedActions, "show_detail") && (
          <>
            <ShowDetailAction auditPlan={auditPlan} />
            <DropdownMenuSeparator />
          </>
        )}

        {hasAction(selectedActions, "defer") && (
          <DeferAction auditPlan={auditPlan} deferMutation={deferMutation} />
        )}

        {hasAction(selectedActions, "delete") && (
          <DeleteAction auditPlan={auditPlan} deleteMutation={deleteMutation} />
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

type ShowDetailActionProps = { auditPlan: AuditPlanListItem };
const ShowDetailAction = ({ auditPlan }: ShowDetailActionProps) => (
  <DropdownMenuItem asChild>
    <Link to="/audit-plans/$planId" params={{ planId: auditPlan.id }}>
      <Eye className={cn([ICON_CLASS])} /> View Details
    </Link>
  </DropdownMenuItem>
);

type DeferActionProps = {
  auditPlan: AuditPlanListItem;
  deferMutation: UseMutationResult<{}, Error, string, unknown>;
};
const DeferAction = ({ auditPlan, deferMutation }: DeferActionProps) => (
  <DropdownMenuItem>
    <button
      className={cn([BUTTON_BASE_CLASS])}
      onClick={() => {
        if (confirm("Do you really want to defer the audit plan?")) {
          deferMutation.mutate(auditPlan.id);
        }
      }}
    >
      <ArrowBigRightDash className={cn([ICON_CLASS])} /> Defer Plan
    </button>
  </DropdownMenuItem>
);

type DeleteActionProps = {
  auditPlan: AuditPlanListItem;
  deleteMutation: UseMutationResult<string, Error, string>;
};
const DeleteAction = ({ deleteMutation, auditPlan }: DeleteActionProps) => (
  <DropdownMenuItem>
    <button
      className={cn([
        BUTTON_BASE_CLASS,
        " text-red-600 focus:text-red-600 focus:bg-red-50",
      ])}
      onClick={() => {
        if (confirm("Do you really want to delete the audit plan?")) {
          deleteMutation.mutate(auditPlan.id);
        }
      }}
    >
      <Trash2 className={cn([ICON_CLASS])} /> Delete Plan
    </button>
  </DropdownMenuItem>
);

const hasAction = (selected: ReadonlyArray<Actions>, action: Actions): boolean => {
  return selected.includes(action);
};
